import { useEffect } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Apple-style scroll-driven frame sequence.
 *
 * Why this exists: setting `video.currentTime = progress * duration` on a
 * `<video>` element forces the browser to seek + decode on every scroll
 * frame, which stutters because H.264 can only random-access at keyframes.
 * Apple product pages render a pre-decoded JPG sequence to a `<canvas>`
 * instead — every frame is already decoded, so `drawImage` is free.
 *
 * This hook supports two sources:
 * - `frames` — preloaded URLs (production R2 or demo build-time assets)
 * - `video`  — runtime extraction (demo fallback when build-time JPGs are
 *               missing). Extraction happens once, frames are kept as
 *               `ImageBitmap` in memory.
 */
export type FrameSource =
  | { kind: "frames"; urls: string[] }
  | { kind: "video"; url: string; count: number };

export interface UseScrollFrameSequenceOptions {
  containerRef: React.RefObject<HTMLElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  source: FrameSource;
  /** Pin distance in px. Takes precedence over `pinViewports` when set. */
  pinDistancePx?: number;
  /** Pin distance in viewport heights (window.innerHeight). Default 2.5. */
  pinViewports?: number;
  /** Called on every frame change with progress 0–1. Use for overlay sync. */
  onProgress?: (progress: number) => void;
  /** Re-run when this token changes (e.g. layout id). */
  resetKey?: string;
}

type DrawableFrame = HTMLImageElement | ImageBitmap;

function frameSize(frame: DrawableFrame): { width: number; height: number } {
  if (frame instanceof HTMLImageElement) {
    return { width: frame.naturalWidth, height: frame.naturalHeight };
  }
  return { width: frame.width, height: frame.height };
}

async function loadImageFrames(urls: string[]): Promise<HTMLImageElement[]> {
  const frames: HTMLImageElement[] = urls.map((src) => {
    const img = new Image();
    img.decoding = "async";
    img.src = src;
    return img;
  });
  // First frame must be ready before initial paint — others can stream in.
  await new Promise<void>((resolve) => {
    const first = frames[0];
    if (!first) return resolve();
    if (first.complete) return resolve();
    first.addEventListener("load", () => resolve(), { once: true });
    first.addEventListener("error", () => resolve(), { once: true });
  });
  return frames;
}

async function extractFramesFromVideo(
  url: string,
  count: number,
): Promise<ImageBitmap[]> {
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = url;

  await new Promise<void>((resolve, reject) => {
    video.addEventListener("loadedmetadata", () => resolve(), { once: true });
    video.addEventListener("error", () => reject(new Error("Video load failed")), { once: true });
  });

  const bitmaps: ImageBitmap[] = [];
  for (let i = 0; i < count; i++) {
    const t = (video.duration / Math.max(1, count - 1)) * i;
    video.currentTime = t;
    await new Promise<void>((resolve) =>
      video.addEventListener("seeked", () => resolve(), { once: true }),
    );
    const bitmap = await createImageBitmap(video);
    bitmaps.push(bitmap);
  }
  return bitmaps;
}

export function useScrollFrameSequence({
  containerRef,
  canvasRef,
  source,
  pinDistancePx,
  pinViewports,
  onProgress,
  resetKey,
}: UseScrollFrameSequenceOptions) {
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const count = source.kind === "frames" ? source.urls.length : source.count;
    if (count === 0) return;

    let frames: DrawableFrame[] = [];
    let cancelled = false;
    let st: gsap.core.Tween | null = null;
    let currentIdx = -1;

    function renderFrame(idx: number) {
      const frame = frames[idx];
      if (!frame) return;
      const { width, height } = frameSize(frame);
      if (canvas!.width !== width) canvas!.width = width;
      if (canvas!.height !== height) canvas!.height = height;
      ctx!.drawImage(frame as CanvasImageSource, 0, 0);
    }

    function startScroll() {
      if (cancelled) return;
      renderFrame(0);
      const obj = { frame: 0 };
      const pin =
        pinDistancePx ??
        Math.round((pinViewports ?? 2.5) * (typeof window !== "undefined" ? window.innerHeight : 800));
      st = gsap.to(obj, {
        frame: count - 1,
        snap: "frame",
        ease: "none",
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: `+=${pin}px`,
          scrub: 0.5,
          pin: true,
        },
        onUpdate() {
          const idx = Math.round(obj.frame);
          if (idx !== currentIdx) {
            currentIdx = idx;
            renderFrame(idx);
          }
          if (onProgress) onProgress(obj.frame / Math.max(1, count - 1));
        },
      });
    }

    (async () => {
      try {
        if (source.kind === "frames") {
          frames = await loadImageFrames(source.urls);
        } else {
          frames = await extractFramesFromVideo(source.url, source.count);
        }
        if (cancelled) return;
        startScroll();
      } catch (err) {
        console.error("useScrollFrameSequence: failed to load frames", err);
      }
    })();

    return () => {
      cancelled = true;
      st?.kill();
      st?.scrollTrigger?.kill();
      // Release ImageBitmaps to free GPU/VRAM
      if (source.kind === "video") {
        frames.forEach((f) => {
          if (f instanceof ImageBitmap) f.close();
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    containerRef,
    canvasRef,
    source.kind,
    source.kind === "frames" ? source.urls.join(",") : source.url,
    source.kind === "video" ? source.count : source.urls.length,
    pinDistancePx,
    pinViewports,
    resetKey,
  ]);
}
